/**
 * Which sites are at the end of onboarding (ENG-2457). A site created in the
 * Add Site flow is remembered here; once its first sync succeeds, the site
 * page shows the camera check as the last onboarding step, until the user
 * closes it. localStorage, like the rest of the onboarding flow's hand-offs.
 */

const KEY = "connectivityOnboardingSites";
/** Forget a site that never finished onboarding after this long. */
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

export interface OnboardingEntry {
  startedAt: string;
  /** First successful sync after onboarding; the connector starts then. */
  syncedAt: string | null;
}

type Entries = Record<string, OnboardingEntry>;

const read = (): Entries => {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? "{}");
    return parsed && typeof parsed === "object" ? (parsed as Entries) : {};
  } catch {
    return {};
  }
};

const write = (entries: Entries) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    // Storage full or blocked: the check is still on the site page's banner.
  }
};

export const markSiteOnboarding = (
  siteId: number | string,
  now: Date = new Date()
) => {
  const entries = read();
  const cutoff = now.getTime() - MAX_AGE_MS;
  Object.keys(entries).forEach((id) => {
    if (Date.parse(entries[id].startedAt) < cutoff) delete entries[id];
  });
  entries[String(siteId)] = { startedAt: now.toISOString(), syncedAt: null };
  write(entries);
};

/** Called after a successful sync; only affects a site still onboarding. */
export const markOnboardingSynced = (
  siteId: number | string,
  now: Date = new Date()
) => {
  const entries = read();
  const entry = entries[String(siteId)];
  if (!entry || entry.syncedAt) return;
  entries[String(siteId)] = { ...entry, syncedAt: now.toISOString() };
  write(entries);
};

export const getOnboardingEntry = (
  siteId: number | string
): OnboardingEntry | null => read()[String(siteId)] ?? null;

export const finishOnboarding = (siteId: number | string) => {
  const entries = read();
  delete entries[String(siteId)];
  write(entries);
};
